// EstimateBuilder (job page round 2, Amir 26 Oct point 9, concept depth): building an estimate from the
// job page, presenting it and getting it signed, as one page over the job (a sheet with Close). Built
// from the leading product's estimate flow and the iPad's own Options and Present screens
// (pages/job-page-r2/st-estimates.md), in three steps said as words at the top, Build · Present · Sign:
//   Build: the estimate's head (its name, the line the customer reads, the office's note, follow up,
//     good until, sold by, tax exempt); its options side by side (OptionEditor: up to three across, four
//     to six one under another, never seven), the price book beside them (ItemPicker: Add to B); the
//     foot: Save the draft, and the one main button, Present to Jordan.
//   Present: in person on this screen or sent as a link; the customer's view (EstimateOptions).
//   Sign: what it does to the work (adds to it or replaces it), done now or booked for later, the
//     deposit, and the signature (SignatureField): nothing is sold by a press alone.
// Red only for a problem: a price changed without a reason, the margin under the company's floor (the
// owner's OK first). An edit after it was presented takes it back to Draft and says so. Its own history
// sits under it. The price book round (DEC-019): beside the options, Start from (the job type's sets,
// jobs like this, this address's past quotes, a blank option: each one row with up to three pictures,
// critic K17) above One Find, whose head says which option it adds to (critic K8); a line can be
// swapped for its neighbour in pictures, and an option shows what usually goes with its lines. A CSR
// on a call builds it too, but never types a price (canPrice). The app's builder gives it what it does
// (`act`): a line's quantity, price and reason, adding from the book, the head's words, Save the draft
// and the main button; each option's own fields and links; the price book searched on the server. What
// the app doesn't keep (follow up, good until, the office's note) is left out. Reads only --estbuild-*;
// every piece reads its own.
import { useState, type ReactNode } from 'react';
import { Button } from '../../components/Button/Button.tsx';
import { Checkbox } from '../../components/Checkbox/Checkbox.tsx';
import { Close } from '../../components/Close/Close.tsx';
import { TextField } from '../../components/Field/Field.tsx';
import { HistoryItem } from '../../components/HistoryItem/HistoryItem.tsx';
import {
  ItemPicker,
  type ItemPickerProps,
  type PickerItem,
} from '../../components/ItemPicker/ItemPicker.tsx';
import { KeyValue, type KeyValueItem } from '../../components/KeyValue/KeyValue.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { OptionColumns, OptionEditor, type EditorLine } from '../../components/OptionEditor/OptionEditor.tsx';
import { Picture, type PictureSrc } from '../../components/Picture/Picture.tsx';
import { PictureChoice, type PictureOption } from '../../components/PictureChoice/PictureChoice.tsx';
import { Segmented } from '../../components/Segmented/Segmented.tsx';
import { SignatureField, type SignatureValue } from '../../components/SignatureField/SignatureField.tsx';
import type { TotalsRow } from '../../components/Totals/Totals.tsx';
import { EstimateOptions, type EstimateOption } from '../EstimateOptions/EstimateOptions.tsx';
import './EstimateBuilder.css';

export type BuilderStep = 'build' | 'present' | 'sign';

export interface BuilderOption {
  id: string;
  letter: string;
  tier?: string;
  title: string;
  summary?: string;
  isRecommended?: boolean;
  lines: EditorLine[];
  totals: TotalsRow[];
  margin?: { text: string; isBad?: boolean };
  /** The customer's view of it, for Present. */
  shown: EstimateOption;
  /** A line's neighbours, to swap it for (same task, same kind), by the line's id. */
  swaps?: Record<string, PictureOption[]>;
  /** What usually goes with its lines, to add with one press. */
  goes?: PictureOption[];
  /** Under its head: its own fields (the app's: title, tier, what it includes). */
  fields?: ReactNode;
  /** Its links after Add a line, in place of Add a discount and Copy this option. */
  actions?: ReactNode;
}

/** One way to start: a set for the job type, jobs like this, this address's past quotes. */
export interface StartSet {
  id: string;
  name: string;
  note: string;
  pictures: PictureSrc[];
}

export interface EstimateBuilderView {
  /** "Job 24612" (the crumb back) and the estimate's own name and number. */
  job: string;
  number: string;
  name: string;
  customer: string;
  /** Said in the head: "Changed after Jordan saw it: back to a draft". */
  stateLine: string;
  head: {
    summary: string;
    /** Left out, its field goes (the app keeps no office note on an estimate). */
    officeNote?: string;
    followUp?: string;
    goodUntil?: string;
    soldBy?: string;
    taxExempt?: boolean;
  };
  options: BuilderOption[];
  picker: {
    groups: { id: string; label: string; count?: number }[];
    items: PickerItem[];
    suggested?: PickerItem[];
    /** One Find's own: before you type, the shelf, the nearest, Ask the office. */
    find?: Pick<ItemPickerProps, 'before' | 'shelf' | 'tries' | 'nearest' | 'onAsk' | 'placeholder' | 'foot'>;
    /** The book searched on the server: the query and the kind as they change (items are then as found). */
    onQuery?: (query: string) => void;
    onGroup?: (group: string) => void;
  };
  /** Start from, above the price book: the sets, and a blank option. With `chosen`, one is in use. */
  startFrom?: {
    sets: StartSet[];
    chosen?: string;
    onChoose: (id: string) => void;
    /** Under the list: "Priya's address has no quotes before." */
    note?: string;
  };
  /** Who may change a price from the book's (a CSR may not): the price then reads, it doesn't edit. */
  canPrice?: boolean;
  /** The main button's words at Build ("Send to Priya"); "Present to Jordan" by default. */
  presentLabel?: string;
  /** A problem the whole estimate has, in red ("Option B’s margin is under 35%: it needs the owner’s OK"). */
  problem?: string;
  history: { id: string; time: string; head: ReactNode; detail?: string }[];
  /** Sign: what the deposit is, and the money. */
  sign: { option: string; total: string; deposit: string; signedAt?: string };
  /** Only the owner sees the margins. */
  showMargin: boolean;
}

/** What the builder does, given by the app (Storybook leaves it out: the presses go nowhere). */
export interface BuilderActs {
  onQty?: (option: string, line: string, qty: string) => void;
  onPrice?: (option: string, line: string, price: string) => void;
  onReason?: (option: string, line: string, reason: string) => void;
  /** An item from the book (or what goes with a line) onto an option. */
  onAdd?: (option: string, item: string) => void;
  onSummary?: (summary: string) => void;
  onNote?: (note: string) => void;
  onSave?: () => void;
  /** "Saving…" while it saves. */
  saveLabel?: string;
  /** The main button, in place of going to Present. */
  onPrimary?: () => void;
  isBusy?: boolean;
  /** In the foot before the main button: what to fix first (bad, in red), or what the save did. */
  footNote?: { text: string; isBad?: boolean };
  /** The option the price book adds to changed. */
  onFocus?: (option: string) => void;
  /** In the foot after Save the draft: Add an option. */
  footLinks?: ReactNode;
}

export interface EstimateBuilderProps {
  view: EstimateBuilderView;
  act?: BuilderActs;
  step: BuilderStep;
  onStep?: (s: BuilderStep) => void;
  /** The steps offered (all three when absent); with one, no step row is drawn. */
  steps?: BuilderStep[];
  onClose: () => void;
  /** Storybook: start signed, typed, or with the price book searched. */
  start?: { signature?: SignatureValue; query?: string; focused?: string; how?: 'person' | 'link' };
}

const STEPS: { id: BuilderStep; label: string }[] = [
  { id: 'build', label: 'Build' },
  { id: 'present', label: 'Present' },
  { id: 'sign', label: 'Sign' },
];

export function EstimateBuilder({
  view: v,
  act,
  step,
  onStep,
  steps,
  onClose,
  start = {},
}: EstimateBuilderProps) {
  const offered = steps ? STEPS.filter((s) => steps.includes(s.id)) : STEPS;
  const [query, setQuery] = useState(start.query ?? '');
  const [group, setGroup] = useState(v.picker.groups[0]?.id ?? 'all');
  const [focused, setOwnFocus] = useState(
    start.focused ?? v.options.find((o) => o.isRecommended)?.id ?? v.options[0]?.id,
  );
  const setFocused = (id: string) => {
    setOwnFocus(id);
    act?.onFocus?.(id);
  };
  const [added, setAdded] = useState(false);
  const [swapping, setSwapping] = useState<string | null>(null);
  const [allStarts, setAllStarts] = useState(false);
  const [swapTo, setSwapTo] = useState<Record<string, string>>({});
  const [how, setHow] = useState<'person' | 'link'>(start.how ?? 'person');
  const [work, setWork] = useState('adds');
  const [when, setWhen] = useState('now');
  const [signature, setSignature] = useState<SignatureValue>(start.signature ?? null);
  const [tried, setTried] = useState(false);
  const [presentTried, setPresentTried] = useState(false);
  // a price changed from the price book with no reason yet holds Present back: pressing it goes there
  const unexplained = v.options.find((o) => o.lines.some((l) => l.wasPrice && !l.reason));
  const [ownSummary, setSummary] = useState(v.head.summary);
  const [ownNote, setNote] = useState(v.head.officeNote ?? '');
  // the app's words are its own (controlled); Storybook's are kept here
  const summary = act?.onSummary ? v.head.summary : ownSummary;
  const note = act?.onNote ? (v.head.officeNote ?? '') : ownNote;
  const [deposit, setDeposit] = useState(v.sign.deposit);
  const f = v.options.find((o) => o.id === focused);
  const items = v.picker.onQuery
    ? v.picker.items
    : v.picker.items.filter(
        (i) =>
          !query.trim() || `${i.name} ${i.code ?? ''}`.toLowerCase().includes(query.trim().toLowerCase()),
      );
  const first = v.customer.split(' ')[0];

  const headFacts: KeyValueItem[] = (
    [
      ['Follow up', v.head.followUp],
      ['Good until', v.head.goodUntil],
      ['Sold by', v.head.soldBy],
    ] as const
  )
    .filter(([, value]) => value)
    .map(([label, value]) => ({ label, value: value! }));

  return (
    <article className="fl-estbuild" aria-labelledby="est-title">
      <header className="fl-estbuild__top">
        <nav className="fl-estbuild__crumb" aria-label="Where you came from">
          <Link onPress={onClose}>{v.job}</Link>
          <span aria-hidden="true">›</span>
          <span>{v.number}</span>
        </nav>
        {offered.length > 1 ? (
          <ol className="fl-estbuild__steps" aria-label="Steps">
            {offered.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="fl-estbuild__step"
                  aria-current={s.id === step ? 'step' : undefined}
                  onClick={() => onStep?.(s.id)}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ol>
        ) : null}
        <Close placement="inline" label={`Close ${v.number}`} onPress={onClose} />
      </header>
      <div className="fl-estbuild__head">
        <h1 id="est-title" className="fl-estbuild__title">
          {v.name}
        </h1>
        <p className="fl-estbuild__sub">
          For {v.customer}:{' '}
          {step === 'build' ? v.stateLine : step === 'present' ? 'presenting it now' : 'signing it now'}
        </p>
        {v.problem ? <p className="fl-estbuild__bad">{v.problem}</p> : null}
      </div>

      {step === 'build' ? (
        <>
          <section className="fl-estbuild__fields" aria-label="The estimate">
            <TextField
              label={`What ${first} reads at the top`}
              value={summary}
              multiline
              onChange={act?.onSummary ?? setSummary}
            />
            {v.head.officeNote !== undefined ? (
              <TextField
                label="For the office only"
                description={`${first} never sees it.`}
                value={note}
                multiline
                onChange={act?.onNote ?? setNote}
              />
            ) : null}
            {headFacts.length || v.head.taxExempt !== undefined ? (
              <div className="fl-estbuild__facts">
                {headFacts.length ? <KeyValue items={headFacts} density="tight" /> : null}
                {v.head.taxExempt !== undefined ? (
                  <Checkbox defaultSelected={v.head.taxExempt}>Tax exempt</Checkbox>
                ) : null}
              </div>
            ) : null}
          </section>
          <div className="fl-estbuild__work">
            <OptionColumns label="Options">
              {v.options.map((o) => (
                <OptionEditor
                  key={o.id}
                  letter={o.letter}
                  tier={o.tier}
                  title={o.title}
                  summary={o.summary}
                  isRecommended={o.isRecommended}
                  isFocused={o.id === focused}
                  totals={o.totals}
                  margin={v.showMargin ? o.margin : undefined}
                  customer={first}
                  level={2}
                  onFocus={() => setFocused(o.id)}
                  onQty={(l, q) => act?.onQty?.(o.id, l, q)}
                  onPrice={v.canPrice === false ? undefined : (l, p) => act?.onPrice?.(o.id, l, p)}
                  onReason={(l, r) => act?.onReason?.(o.id, l, r)}
                  fields={o.fields}
                  lines={o.lines.map((l) => {
                    const near = o.swaps?.[l.id];
                    if (!near?.length) return l;
                    const isOpen = swapping === `${o.id}:${l.id}`;
                    return {
                      ...l,
                      below: (
                        <>
                          <Link onPress={() => setSwapping(isOpen ? null : `${o.id}:${l.id}`)}>
                            {isOpen ? 'Keep this one' : 'Swap'}
                          </Link>
                          {isOpen ? (
                            <PictureChoice
                              mode="one"
                              label={`Swap ${l.name} for`}
                              hideLabel
                              options={near}
                              value={swapTo[l.id] ?? l.id}
                              onChange={(id) => setSwapTo((t) => ({ ...t, [l.id]: id }))}
                            />
                          ) : null}
                        </>
                      ),
                    };
                  })}
                  suggest={
                    o.goes?.length ? (
                      <PictureChoice
                        mode="add"
                        label="Usually goes with it"
                        addLabel={`Add to ${o.letter}`}
                        options={o.goes}
                        onAdd={(id) => {
                          setFocused(o.id);
                          act?.onAdd?.(o.id, id);
                        }}
                      />
                    ) : undefined
                  }
                  actions={
                    <>
                      <Link onPress={() => setFocused(o.id)}>Add a line</Link>
                      {o.actions ?? (
                        <>
                          <Link onPress={() => undefined}>Add a discount</Link>
                          <Link onPress={() => undefined}>Copy this option</Link>
                        </>
                      )}
                    </>
                  }
                />
              ))}
            </OptionColumns>
            <aside className="fl-estbuild__book" aria-label="The price book">
              {v.startFrom ? (
                <section className="fl-estbuild__start" aria-labelledby="est-start">
                  <h2 id="est-start" className="fl-estbuild__starth">
                    Start from
                  </h2>
                  <ul className="fl-estbuild__starts">
                    {[
                      ...v.startFrom.sets,
                      {
                        id: 'blank',
                        name: 'A blank option',
                        note: 'Find each line in the book',
                        pictures: [],
                      },
                    ]
                      // once one is chosen the list folds to it (critic K17); Other ways to start opens it again
                      .filter((st) => allStarts || !v.startFrom!.chosen || st.id === v.startFrom!.chosen)
                      .map((st) => (
                        <li key={st.id}>
                          <button
                            type="button"
                            className="fl-estbuild__startrow"
                            aria-pressed={v.startFrom!.chosen === st.id}
                            onClick={() => v.startFrom!.onChoose(st.id)}
                          >
                            <span className="fl-estbuild__pics" aria-hidden="true">
                              {st.pictures.length ? (
                                st.pictures.slice(0, 3).map((p, n) => <Picture key={n} src={p} size="xs" />)
                              ) : (
                                <Picture size="xs" state="none" />
                              )}
                            </span>
                            <span className="fl-estbuild__startname">{st.name}</span>
                            <span className="fl-estbuild__startnote">{st.note}</span>
                          </button>
                        </li>
                      ))}
                  </ul>
                  {v.startFrom.chosen ? (
                    <p className="fl-estbuild__startmore">
                      <Link onPress={() => setAllStarts((a) => !a)}>
                        {allStarts ? 'Just the one in use' : 'Other ways to start'}
                      </Link>
                    </p>
                  ) : null}
                  {v.startFrom.note ? <p className="fl-estbuild__startnote">{v.startFrom.note}</p> : null}
                </section>
              ) : null}
              <ItemPicker
                label="The price book"
                query={query}
                onQueryChange={(q) => {
                  setQuery(q);
                  v.picker.onQuery?.(q);
                }}
                groups={v.picker.groups}
                group={group}
                onGroupChange={(g) => {
                  setGroup(g);
                  v.picker.onGroup?.(g);
                }}
                items={items}
                addLabel={`Add to ${f?.letter ?? 'B'}`}
                onAdd={(id) => {
                  setAdded(true);
                  if (f) act?.onAdd?.(f.id, id);
                }}
                {...v.picker.find}
                adding={
                  v.options.length > 1
                    ? {
                        label: 'Adding to',
                        items: v.options.map((o) => ({
                          id: o.id,
                          label: o.tier ? `${o.letter} · ${o.tier}` : o.letter,
                        })),
                        value: focused ?? v.options[0]!.id,
                        onChange: setFocused,
                      }
                    : undefined
                }
                suggested={
                  added && v.picker.suggested
                    ? { title: 'Often added with it', items: v.picker.suggested }
                    : undefined
                }
              />
            </aside>
          </div>
          <footer className="fl-estbuild__foot">
            <Link onPress={() => act?.onSave?.()}>{act?.saveLabel ?? 'Save the draft'}</Link>
            {act?.footLinks}
            {presentTried && unexplained ? (
              <p className="fl-estbuild__bad" role="alert">
                Option {unexplained.letter} has a changed price with no reason yet: give the reason first.
              </p>
            ) : act?.footNote ? (
              <p
                className={act.footNote.isBad ? 'fl-estbuild__bad' : 'fl-estbuild__sub'}
                role={act.footNote.isBad ? 'alert' : 'status'}
              >
                {act.footNote.text}
              </p>
            ) : null}
            <Button
              variant="primary"
              isBusy={act?.isBusy}
              onPress={() => {
                if (!unexplained) return act?.onPrimary ? act.onPrimary() : onStep?.('present');
                setPresentTried(true);
                setFocused(unexplained.id);
              }}
            >
              {v.presentLabel ?? `Present to ${first}`}
            </Button>
          </footer>
        </>
      ) : null}

      {step === 'present' ? (
        <>
          <section className="fl-estbuild__fields" aria-label="How it’s presented">
            <div className="fl-estbuild__choice">
              <p className="fl-estbuild__sub" aria-hidden="true">
                How it’s presented
              </p>
              <Segmented
                label="How"
                selectedKey={how}
                onSelectionChange={(k) => setHow(k as 'person' | 'link')}
                items={[
                  { id: 'person', label: 'In person, on this screen' },
                  { id: 'link', label: `Send ${first} a link` },
                ]}
              />
            </div>
            <p className="fl-estbuild__sub">
              {how === 'person'
                ? `Turn the screen to ${first}. They choose an option, then sign.`
                : `A text and an email with a link; ${first} chooses and signs on their phone. It says when they open it.`}
            </p>
          </section>
          <EstimateOptions
            options={v.options.map((o) => o.shown)}
            foot={`${summary} · Good until ${v.head.goodUntil}`}
          />
          <footer className="fl-estbuild__foot">
            <Link onPress={() => onStep?.('build')}>Back to building it</Link>
            <Button variant="primary" onPress={() => onStep?.(how === 'person' ? 'sign' : 'build')}>
              {how === 'person'
                ? `${first} chose ${v.sign.option.split(',')[0]!.replace(/^Option/, 'option')}: sign it`
                : `Send it to ${first}`}
            </Button>
          </footer>
        </>
      ) : null}

      {step === 'sign' ? (
        <>
          <section className="fl-estbuild__fields" aria-label="Signing">
            <KeyValue
              items={[
                { label: 'Chosen', value: v.sign.option },
                { label: 'Total', value: <b>{v.sign.total}</b> },
              ]}
            />
            <div className="fl-estbuild__choice">
              <p className="fl-estbuild__sub" aria-hidden="true">
                What it does to the work
              </p>
              <Segmented
                label="What it does to the work"
                selectedKey={work}
                onSelectionChange={setWork}
                items={[
                  { id: 'adds', label: 'Adds to the work' },
                  { id: 'replaces', label: 'Replaces what was sold' },
                ]}
              />
            </div>
            <div className="fl-estbuild__choice">
              <p className="fl-estbuild__sub" aria-hidden="true">
                When
              </p>
              <Segmented
                label="When"
                selectedKey={when}
                onSelectionChange={setWhen}
                items={[
                  { id: 'now', label: 'Do the work now' },
                  { id: 'later', label: 'Book it for later' },
                ]}
              />
            </div>
            <TextField label="Deposit taken now" value={deposit} onChange={setDeposit} />
            <SignatureField
              signer={v.customer}
              value={signature}
              onChange={setSignature}
              signedAt={signature ? v.sign.signedAt : undefined}
              error={tried && !signature ? `${first} signs before it can be sold.` : undefined}
              defaultMode={start.signature?.kind === 'typed' ? 'type' : 'draw'}
            />
          </section>
          <footer className="fl-estbuild__foot">
            <Link onPress={() => onStep?.('present')}>Back to the options</Link>
            <Button variant="primary" onPress={() => setTried(true)}>
              {when === 'now' ? 'Sold: do the work now' : 'Sold: book it for later'}
            </Button>
          </footer>
        </>
      ) : null}

      {v.history.length ? (
        <section className="fl-estbuild__history" aria-labelledby="est-history">
          <h2 id="est-history" className="fl-estbuild__h2">
            This estimate’s history
          </h2>
          <div>
            {v.history.map((h) => (
              <HistoryItem key={h.id} kind="estimate" time={h.time} detail={h.detail}>
                {h.head}
              </HistoryItem>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
